import React, { useEffect } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate,
  createBrowserRouter,
  RouterProvider 
} from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
// import EmployeeDashboard from './components/EmployeeDashboard'; // DEPRECATED: Using UnifiedDashboard now
import CompanyLogin from './components/CompanyLogin';
import CompanyDashboard from './components/CompanyDashboard';
import MainLogin from './components/MainLogin';
import UnifiedLogin from './components/UnifiedLogin';
import CompanyRegistration from './components/CompanyRegistration';
import { cleanupStaleNotificationsOnStartup } from './utils/notificationCleanup';
import { AuthProvider, useAuth } from './context/AuthContext';
import Register from './components/Register';
import './App.css';
import AutoCaptureForm from './components/AutoCaptureForm';
import LeadProfile from './components/LeadProfile';
import DemoLogin from './components/DemoLogin';
import SalesTeamTest from './components/SalesTeamTest';
import EmployeeLogin from './components/EmployeeLogin';
import EmployeeDashboardNew from './components/EmployeeDashboardNew';
import BranchAdminDashboard from './components/BranchAdminDashboard';
import BranchDashboard from './components/BranchDashboard';
import BranchDashboardNew from './components/BranchDashboardNew';
import EnquiryFlowDashboard from './components/EnquiryFlowDashboard';
import DropdownOptionsManager from './components/DropdownOptionsManager';
import UnifiedDashboard from './components/UnifiedDashboard';
import OtherServicesDashboard from './components/OtherServicesDashboard';

// Protected Route Component
const ProtectedRoute = ({ children, allowedUserTypes = ['user', 'superadmin', 'employee'] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user type is allowed for this route
  const userType = user?.userType || user?.type || user?.role;
  if (user && !allowedUserTypes.includes(userType)) {
    // Redirect to appropriate dashboard based on user type
    if (userType === 'employee') {
      return <Navigate to="/unified-dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }
  
  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (isAuthenticated && user) {
    // Redirect to appropriate dashboard based on user type
    const userType = user?.userType || user?.type || user?.role;
    if (userType === 'employee') {
      return <Navigate to="/unified-dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }
  
  return children;
};

function App() {
  // Clean up stale notifications on app startup
  useEffect(() => {
    cleanupStaleNotificationsOnStartup();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Main Login Route - Unified */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <UnifiedLogin />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            
            {/* Auto Capture Form - Public Route */}
            <Route 
              path="/capture" 
              element={<AutoCaptureForm />} 
            />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute allowedUserTypes={['user', 'superadmin', 'company']}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Employee Dashboard Route - DEPRECATED: Now using /unified-dashboard */}
            
            {/* Lead Profile Route */}
            <Route 
              path="/lead/:id" 
              element={
                <ProtectedRoute>
                  <LeadProfile />
                </ProtectedRoute>
              } 
            />
            
            {/* Sales Team Test Route */}
            <Route 
              path="/sales-test" 
              element={
                <ProtectedRoute>
                  <SalesTeamTest />
                </ProtectedRoute>
              } 
            />
            
            {/* Enquiry Flow Dashboard Route */}
            <Route 
              path="/enquiry-flow" 
              element={
                <ProtectedRoute>
                  <EnquiryFlowDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Unified Dashboard Route */}
            <Route 
              path="/unified-dashboard" 
              element={
                <ProtectedRoute>
                  <UnifiedDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Dropdown Options Manager Route */}
            <Route 
              path="/dropdown-options" 
              element={
                <ProtectedRoute allowedUserTypes={['user', 'superadmin', 'company']}>
                  <DropdownOptionsManager />
                </ProtectedRoute>
              } 
            />
            
            {/* Employee Login Route */}
            <Route 
              path="/employee-login" 
              element={<EmployeeLogin />} 
            />
            
            {/* New Employee Dashboard Route */}
            <Route 
              path="/employee-dashboard-new" 
              element={<EmployeeDashboardNew />} 
            />
            
            {/* Branch Admin Dashboard Route */}
            <Route 
              path="/branch-admin-dashboard" 
              element={<BranchAdminDashboard />} 
            />
            
            {/* Branch Dashboard Route - Public (has its own auth check) */}
            <Route 
              path="/branch-dashboard" 
              element={<BranchDashboardNew />} 
            />
            
            {/* Other Services Dashboard Route */}
            <Route 
              path="/other-services-dashboard" 
              element={<OtherServicesDashboard />} 
            />
            
            {/* Company Registration Route */}
            <Route 
              path="/company-registration" 
              element={<CompanyRegistration />} 
            />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;